"use client"

import { ADMIN_API_CONFIG } from "@/constants/api-configs"
import { DefaultImage } from "@/lib/external-components"
import { generateImagePath } from "@/lib/utils"
import { formatDate } from "@/utils/formatDate"
import { ComponentConfig } from "@measured/puck"
import dayjs from "dayjs"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import "swiper/css"
import "swiper/css/pagination"
import { twMerge } from "tailwind-merge"
import { fetchNewestPostsByLocale } from "../../../../../post/services/post"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"
import { PostData, TagData } from "../../../types"
export interface ArticleItem {
  image1?: string
  image2?: string
  time?: Date
  itemTitle?: string
  excerpt?: string
  tags?: TagData
}

export interface ArticlesProps extends CommonStylesProps {
  type: "fluid" | "custom" | "boxed"
  gapCustom?: string
  heightContact?: string
  category?: { post?: number }
  locale?: string
}

export const RenderSinglePost: ComponentConfig<ArticlesProps> = {
  render: ({ className, category, styles, gapCustom, responsiveType, locale }) => {
    const id = `navigation-${Math.random().toString(36).substr(2, 9)}`
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)
    const [singleArticle, setSingleArticle] = useState<PostData>()
    const router = useRouter()
    const lang = typeof window !== "undefined" ? document.documentElement.lang || "en" : "en"

    const formattedDate = (date: string | Date) => {
      return dayjs(date).format("MMM D, YYYY")
    }
    const shortDescription = singleArticle?.attributes.excerpt
      ? singleArticle?.attributes.excerpt
      : singleArticle?.attributes.content.slice(0, 200) + "..."

    const fetchNewestPost = async () => {
      try {
        const response = await fetchNewestPostsByLocale(lang || "en")
        if (response?.data && response.data.length > 0) {
          setSingleArticle(response.data[0])
        }
      } catch (error) {
        console.error("Error fetching newest post:", error)
      }
    }

    useEffect(() => {
      fetchNewestPost()
    }, [locale])
    return (
      <>
        {responsiveCSS}
        <div id={id} className={twMerge(className, "w-full max-w-[1440px] flex justify-center")}>
          <Link
            href={`/${lang}/blog/${singleArticle?.attributes.slug}`}
            className="w-full flex lg:flex-row flex-col lg:gap-16 gap-6 cursor-pointer group"
          >
            <div className="overflow-hidden rounded-2xl lg:w-[543px] lg:h-[300px] w-full h-[250px] flex-shrink-0">
              <Image
                src={
                  singleArticle?.attributes.image.data?.attributes.url
                    ? generateImagePath(singleArticle.attributes.image.data.attributes.url, "medium")
                    : DefaultImage
                }
                alt={
                  singleArticle?.attributes.image.data?.attributes.alternativeText ||
                  singleArticle?.attributes.name ||
                  ""
                }
                width={543}
                height={327}
                className="rounded-2xl w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
              />
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex flex-row gap-2 items-center">
                {singleArticle?.attributes.tags.data?.map((tag, index) => (
                  <p
                    key={index}
                    className="py-1.5 px-2 rounded-lg bg-[#0B9444] text-white uppercase text-sm font-semibold transition-all duration-300 group-hover:bg-[#0a7d39]"
                    style={{ lineHeight: "16px" }}
                  >
                    {tag.attributes.name}
                  </p>
                ))}
                <p className="text-[#999999] text-sm">
                  •{" "}
                  {singleArticle?.attributes.createdAt
                    ? formattedDate(singleArticle?.attributes.createdAt)
                    : "Date not available"}
                </p>
              </div>
              <h2 className="text-[#242F3E] font-semibold lg:text-[32px] text-[28px] lg:leading-10 leading-7 transition-colors duration-300 group-hover:text-[#0B9444]">
                {singleArticle?.attributes.name}
              </h2>
              <p
                className="text-[#4F4F4F] lg:text-xl leading-6 text-lg transition-colors duration-300 group-hover:text-[#242F3E]"
                dangerouslySetInnerHTML={{ __html: shortDescription || "" }}
              />
              <div className="w-full flex flex-row items-center justify-between pb-4 border-b border-[#EBEDF0] transition-colors duration-300 group-hover:border-[#0B9444]">
                <p className="text-[#999999] text-sm">
                  {singleArticle?.attributes.createdAt
                    ? formatDate(singleArticle?.attributes.createdAt).formattedDate
                    : "Date not available"}
                </p>
                <Link
                  href={`${ADMIN_API_CONFIG.API_URL}/${singleArticle?.attributes.slug}`}
                  className="text-[#0B9444] text-sm font-medium transition-all duration-300 group-hover:translate-x-1"
                >
                  Read more
                </Link>
              </div>
            </div>
          </Link>
        </div>
      </>
    )
  },
}
